import React, { useState, useEffect } from 'react';
import { BookOpen, Clock, Award, TrendingUp, Play, CheckCircle, Star, Flame, Trophy, Zap, WifiOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BadgeIcon from '../components/BadgeIcon';
import axios from 'axios';

const FALLBACK_ICONS = [Star, Flame, Trophy, Zap, Award];

const Dashboard = () => {
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState(null);
  const [gamificationData, setGamificationData] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      const [progressRes, gamifRes, coursesRes] = await Promise.allSettled([
        axios.get('/api/analytics/my-progress'),
        axios.get('/api/gamification/my-stats'),
        axios.get('/api/courses?limit=20'),
      ]);

      if (progressRes.status === 'fulfilled' && progressRes.value.data?.stats) {
        setProgressData(progressRes.value.data);
      } else {
        setApiError(true);
      }
      if (gamifRes.status === 'fulfilled' && gamifRes.value.data) {
        setGamificationData(gamifRes.value.data);
      }
      if (coursesRes.status === 'fulfilled' && coursesRes.value.data?.courses) {
        setAllCourses(coursesRes.value.data.courses);
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  const stats = progressData?.stats ? [
    {
      icon: BookOpen,
      value: String(progressData.stats.totalEnrolled || 0),
      label: 'Cours suivis',
      color: 'bg-blue-500'
    },
    {
      icon: Clock,
      value: progressData.stats.totalStudyTime
        ? `${Math.round(progressData.stats.totalStudyTime / 60)}h`
        : '0h',
      label: "Temps d'étude",
      color: 'bg-green-500'
    },
    {
      icon: Award,
      value: String(progressData.stats.certificatesEarned || 0),
      label: 'Certificats',
      color: 'bg-yellow-500'
    },
    {
      icon: TrendingUp,
      value: `${progressData.stats.averageProgress || 0}%`,
      label: 'Progression',
      color: 'bg-purple-500'
    },
  ] : [];

  // Cours récents : cours en cours de l'utilisateur (depuis l'API)
  const recentCourses = progressData
    ? (progressData.recentActivity || []).map(e => ({
        id: e.id,
        title: e.course?.title || 'Cours',
        progress: e.progress || 0,
        lastLesson: e.last_lesson_title || '',
        nextLesson: '',
        thumbnail: e.course?.thumbnail || '',
        slug: e.course?.slug || '',
      }))
    : [];

  // Réalisations — vrais badges depuis l'API gamification
  const earnedBadges = gamificationData?.badges?.earned || [];
  const achievements = earnedBadges.length > 0
    ? earnedBadges.map((ub, i) => ({
        title: ub.badge?.name || 'Badge',
        description: ub.badge?.description || '',
        icon: ub.badge?.icon || null,
        Icon: FALLBACK_ICONS[i % FALLBACK_ICONS.length],
      }))
    : (() => {
        const derived = [];
        if (progressData?.stats) {
          if (progressData.stats.completed >= 1)
            derived.push({ title: 'Premier cours terminé', description: 'Vous avez terminé votre premier cours !', icon: null, Icon: Star });
          if (progressData.stats.totalEnrolled >= 5)
            derived.push({ title: 'Étudiant assidu', description: `${progressData.stats.totalEnrolled} cours suivis`, icon: null, Icon: Flame });
          if (progressData.stats.certificatesEarned >= 1)
            derived.push({ title: 'Certifié', description: `${progressData.stats.certificatesEarned} certificat(s) obtenu(s)`, icon: null, Icon: Trophy });
          if (progressData.stats.averageProgress >= 80)
            derived.push({ title: 'Haute progression', description: `${progressData.stats.averageProgress}% de progression moyenne`, icon: null, Icon: Zap });
        }
        return derived;
      })();

  // Recommandations — cours publiés que l'utilisateur ne suit pas encore
  const enrolledIds = new Set(
    (progressData?.enrollments || []).map(e => e.course?.id).filter(Boolean)
  );
  const recommendations = allCourses
    .filter(c => !enrolledIds.has(c.id))
    .slice(0, 3);

  const handleContinueCourse = (courseId, courseTitle, slug) => {
    const courseSlug = slug || courseTitle
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();
    navigate(`/learn/${courseSlug}`);
  };

  const handleDiscoverCourse = (slug) => {
    navigate(`/courses/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center py-20">
        <div className="animate-pulse text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center py-20">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md text-center">
          <WifiOff className="w-10 h-10 mx-auto mb-4 text-gray-400" aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Données indisponibles</h2>
          <p className="text-gray-600 mb-6">
            Impossible de charger vos données de progression. Veuillez réessayer.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-0 sm:px-2 lg:px-4 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-text mb-2">
            Tableau de bord
          </h1>
          <p className="text-gray-600">
            Bienvenue ! Voici un aperçu de votre progression d'apprentissage.
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6">
              <div className="flex items-center">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 ${stat.color} rounded-lg flex items-center justify-center mr-2 sm:mr-4 flex-shrink-0`}>
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-lg sm:text-2xl font-bold text-text">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-600 truncate">{stat.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Cours récents */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-text mb-6">
                Continuez vos cours
              </h2>
              {recentCourses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun cours en cours. Commencez votre apprentissage !</p>
                  <button
                    onClick={() => navigate('/courses')}
                    className="mt-4 text-primary hover:text-primary/80 font-medium text-sm"
                  >
                    Découvrir les cours
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentCourses.map((course) => (
                    <div key={course.id} className="flex items-start gap-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-primary/30 transition-colors duration-200">
                      {course.thumbnail ? (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-14 h-10 sm:w-16 sm:h-12 object-cover rounded flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-10 sm:w-16 sm:h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-text mb-1">{course.title}</h3>
                        {course.lastLesson && (
                          <div className="text-sm text-gray-600 mb-2">
                            <span>Dernière leçon : {course.lastLesson}</span>
                          </div>
                        )}
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm text-gray-600">{course.progress}% terminé</span>
                          <button
                            onClick={() => handleContinueCourse(course.id, course.title, course.slug)}
                            className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium transition-colors duration-200"
                          >
                            <Play className="w-4 h-4" />
                            Continuer
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Réalisations et recommandations */}
          <div className="space-y-6">
            {/* Réalisations */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-text mb-4">
                Vos réalisations
              </h2>
              {achievements.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Commencez un cours pour débloquer des réalisations !
                </p>
              ) : (
                <div className="space-y-3">
                  {achievements.map((achievement, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      {achievement.icon
                        ? <BadgeIcon icon={achievement.icon} className="w-6 h-6 text-primary" />
                        : <achievement.Icon className="w-6 h-6 text-primary" />}
                      <div>
                        <h3 className="font-medium text-text text-sm">{achievement.title}</h3>
                        {achievement.description && (
                          <p className="text-xs text-gray-600">{achievement.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recommandations */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-text mb-4">
                Recommandé pour vous
              </h2>
              {recommendations.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Vous êtes inscrit à tous les cours disponibles !
                </p>
              ) : (
                <div className="space-y-3">
                  {recommendations.map(course => (
                    <div key={course.id} className="p-3 border border-gray-200 rounded-lg">
                      <h3 className="font-medium text-text text-sm mb-1 line-clamp-1">
                        {course.title}
                      </h3>
                      {course.description && (
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          {course.description}
                        </p>
                      )}
                      <button
                        onClick={() => handleDiscoverCourse(course.slug)}
                        className="text-primary hover:text-primary/80 text-xs font-medium transition-colors duration-200"
                      >
                        Découvrir →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Objectifs et planification */}
        <div className="mt-4 sm:mt-6 lg:mt-8 bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <h2 className="text-xl font-bold text-text mb-4">
            Vos objectifs d'apprentissage
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-text mb-1">Cours terminés</h3>
              <p className="text-sm text-gray-600">
                {progressData?.stats.completed || 0} cours complété(s)
              </p>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-text mb-1">Cours en cours</h3>
              <p className="text-sm text-gray-600">
                {progressData?.stats.inProgress || 0} cours actif(s)
              </p>
              <div className="mt-2">
                <span className="text-blue-600 font-medium">En progression</span>
              </div>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-text mb-1">Certificats obtenus</h3>
              <p className="text-sm text-gray-600">
                {progressData?.stats.certificatesEarned || 0} certificat(s)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
